import { useQuery } from "@tanstack/react-query";
import type { JSX } from "react";
import Markdown, { type Options } from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import remarkGfm from "remark-gfm";
import { useORPC } from "../../orpc-react.tsx";
import { TaskDocument, type TaskDocumentIdentifier } from "../../services/task-documents.ts";
import { MermaidDiagram } from "../MermaidDiagram.tsx";
import "highlight.js/styles/github.css";
import "./TaskViewer.css";

export type TaskViewerProps = {
  reactMarkdownOptions?: Options;
  taskId?: TaskDocumentIdentifier;
};

export function TaskViewer(props: TaskViewerProps): JSX.Element {
  const orpc = useORPC();
  const task = useQuery(
    orpc.tasks.read.queryOptions({
      enabled: props.taskId !== undefined,
      input: { id: props.taskId ?? "" },
    }),
  );

  if (!props.taskId) return <p role="alert">A task identifier is required.</p>;
  if (task.isLoading)
    return (
      <div className="task-viewer-loading">
        <p role="status">Loading task...</p>
      </div>
    );
  if (task.isError)
    return (
      <div role="alert">
        <p>{task.error.message}</p>
        <button onClick={() => void task.refetch()} type="button">
          Retry
        </button>
      </div>
    );
  const taskDocument = TaskDocument.safeParse(task.data);
  if (!taskDocument.success)
    return (
      <div role="alert">
        <p>Task {props.taskId} was not found.</p>
        <button onClick={() => void task.refetch()} type="button">
          Retry
        </button>
      </div>
    );

  return (
    <div className="task-viewer">
      <Markdown
        {...props.reactMarkdownOptions}
        components={{
          ...props.reactMarkdownOptions?.components,
          code({ children, className }): JSX.Element {
            if (!children) return <></>;
            if (className && className.includes("language-mermaid"))
              return <MermaidDiagram chart={String(children)} />;
            return <code className={className}>{children}</code>;
          },
        }}
        rehypePlugins={[...(props.reactMarkdownOptions?.rehypePlugins ?? []), rehypeHighlight]}
        remarkPlugins={[...(props.reactMarkdownOptions?.remarkPlugins ?? []), remarkGfm]}
      >
        {taskDocument.data.markdown}
      </Markdown>
    </div>
  );
}
